import SearchParamsHelper from './SearchParamsHelper'
import type SearchParams from '~/types/search/SearchParams'

const ALLOWED_PATHS: string[] = ['/search/', '/lastminute/']
const SSR_PARAMS_PREFIX: string = 'p/'
const CUSTOM_ENCODED_AMPERSAND: string = '__and__'

let pendingHistoryTimer: ReturnType<typeof window.setTimeout> | undefined
let pendingHistoryLocation: string | undefined
let pendingHistoryWriteMode: 'push' | 'replace' = 'push'

class UrlHashSearchParams {
  public get (): SearchParams|undefined {
    const str = window.location.hash

    return this.convertHashParamsStringToSearchParams(str)
  }

  public getSsr (): SearchParams|undefined {
    const pathname = window.location.pathname
    let str = ''
    for (const allowedPath of ALLOWED_PATHS) {
      const prefix = allowedPath + SSR_PARAMS_PREFIX
      if (pathname.startsWith(prefix)) {
        str = pathname.substring(prefix.length)
        break
      }
    }

    return this.convertHashParamsStringToSearchParams(str)
  }

  public set (newPageNumber: number = 0, newSearchParams: SearchParams): void {
    const pathPrefix: string|null = this.getPathPrefix()
    const oldPageNumber = this.getPageNumber()
    const oldSearchParams = this.get()

    if (pathPrefix === null) {
      return
    }

    if (
      oldSearchParams !== undefined &&
      SearchParamsHelper.areSearchParamsObjectsIdentical(oldSearchParams, newSearchParams) &&
      oldPageNumber === newPageNumber
    ) {
      return
    }

    if (
      oldSearchParams === undefined &&
      Object.keys(newSearchParams).length === 0 && newSearchParams.constructor === Object &&
      oldPageNumber === newPageNumber
    ) {
      return
    }

    // TODO: Remove commented out code
    // let newLocation = location.href.split('#')[0].split('?')[0]
    let newLocation = location.protocol + '//' + location.host + pathPrefix
    newLocation += this.convertPageNumberToPathParamString(pathPrefix, newPageNumber)
    newLocation += this.convertSearchParamsToHashParamsString(newSearchParams)

    // let newPath = pathPrefix
    // newPath += this.convertPageNumberToPathParamString(pathPrefix, newPageNumber)
    // const newHash = this.convertSearchParamsToHashParamsString(newSearchParams)

    // Coalesce rapid consecutive updates into a single history entry.
    // This prevents needing multiple back-clicks after applying filters.
    pendingHistoryLocation = newLocation
    pendingHistoryWriteMode = (history.state?.modalOpen === true) ? 'replace' : 'push'

    if (pendingHistoryTimer) {
      clearTimeout(pendingHistoryTimer)
    }

    pendingHistoryTimer = window.setTimeout(() => {
      const locationToWrite = pendingHistoryLocation
      const mode = pendingHistoryWriteMode

      pendingHistoryTimer = undefined
      pendingHistoryLocation = undefined

      if (!locationToWrite) {
        return
      }

      // eslint-disable-next-line no-console
      console.log(mode === 'replace' ? 'Replacing state in history' : 'Pushing state to history')

      if (mode === 'replace') {
        history.replaceState({ qry: locationToWrite }, '', locationToWrite)
      } else {
        history.pushState({ qry: locationToWrite }, '', locationToWrite)
      }

      // eslint-disable-next-line no-console
      console.log('Calling GTM code')

      const { $gtmModules } = useNuxtApp()
      $gtmModules.content.contentView(
        window.location.pathname + window.location.search + window.location.hash,
        'search'
      )
    }, 300)
  }

  public getPathPrefix (): string|null {
    const str = window.location.pathname
    if (str.length < 2) {
      return null
    }
    for (let i = 0; i < ALLOWED_PATHS.length; i++) {
      if (str.startsWith(ALLOWED_PATHS[i])) {
        return ALLOWED_PATHS[i]
      }
    }
    return null
  }

  public hasSsrParamsPrefix (): boolean {
    const str = window.location.pathname
    if (str.length < 2) {
      return false
    }
    const pathPrefix: string|null = this.getPathPrefix()
    if (pathPrefix === null) {
      return false
    }
    return str.startsWith(pathPrefix + SSR_PARAMS_PREFIX)
  }

  public getPageNumber (): number {
    let str = window.location.pathname

    // Remove trailing slash, if it exists
    str = (str.charAt(str.length - 1) === '/') ? str.substring(0, str.length - 1) : str

    const pathnameParts = str.split('/')

    if (pathnameParts.length < 1) {
      return 1
    }

    const lastElement = pathnameParts[pathnameParts.length - 1]
    const pageNumber = parseInt(lastElement)

    if (isNaN(pageNumber)) {
      return 1
    }

    return pageNumber
  }

  private convertPageNumberToPathParamString (pathPrefix: string, pageNumber: number): string {
    if (pageNumber < 2) {
      return ''
    }
    const slashBetweenUrlPrefixAndPageNumber = pathPrefix?.endsWith('/') ? '' : '/'
    const slashAfterPageNumber = pathPrefix?.endsWith('/') ? '/' : ''
    return `${slashBetweenUrlPrefixAndPageNumber}${pageNumber}${slashAfterPageNumber}`
  }

  public convertHashParamsStringToSearchParams (searchParamsString: string): SearchParams|undefined {
    searchParamsString = (searchParamsString.charAt(0) === '#') ? searchParamsString.substring(1) : searchParamsString

    const urlParams = new URLSearchParams(searchParamsString)
    const entries = urlParams.entries()

    const data: SearchParams = {}

    for (const [key, val] of entries) {
      if (val === 'NaN') {
        continue
      }

      const decodedVal = this.decodeValue(val)

      let isArray = false
      let newKey = key
      if (key.endsWith('[]')) {
        isArray = true
        newKey = key.slice(0, -2)
      }
      if (key.endsWith('%255B%255D')) {
        isArray = true
        newKey = key.slice(0, -10)
      }
      if (key.endsWith('%5B%5D')) {
        isArray = true
        newKey = key.slice(0, -6)
      }

      const isNumberValue = [
        'page',
        'rows',
        'kinderbetten',
        'einzelbetten',
        'doppelbetten',
        'einzelzimmer',
        'ausstattung',
        'preis',
        'preis_min',
        'variabilitaet',
        'personen',
        'onlinebuchung',
        'free_cancellation',
        'serp_highlight',
        'distance_strand'
      ].includes(newKey)

      if (!isArray) {
        data[newKey] = isNumberValue ? Number(decodedVal) : decodedVal
      }

      if (!Object.prototype.hasOwnProperty.call(data, newKey)) {
        data[newKey] = []
      }

      if (Array.isArray(data[newKey]) && !data[newKey].includes(decodedVal)) {
        data[newKey].push(
          isNumberValue ? Number(decodedVal) : decodedVal
        )
      }
    }

    if (Object.keys(data).length === 0) {
      return undefined
    }

    return data
  }

  public convertSearchParamsToHashParamsString (searchParams: SearchParams): string {
    const urlParams = new URLSearchParams()

    for (const key in searchParams) {
      // Skip if the property is from prototype
      if (!Object.prototype.hasOwnProperty.call(searchParams, key)) {
        continue
      }

      if (!Array.isArray(searchParams[key])) {
        const theEncodedValue = this.encodeValue(searchParams[key])
        if (urlParams.has(key, theEncodedValue)) {
          continue
        }
        urlParams.append(key, theEncodedValue)
        continue
      }

      for (const theArrayValue of searchParams[key]) {
        const theArrayEncodedValue = this.encodeValue(theArrayValue)
        if (urlParams.has(key + '[]', theArrayEncodedValue)) {
          continue
        }
        urlParams.append(key + '[]', theArrayEncodedValue || '')
      }
    }

    let hash = ''
    if ([...urlParams].length > 0) {
      hash = urlParams.toString()
    }

    return (hash !== '') ? ('#' + hash) : ''
  }

  private encodeValue (theValue: number | string): string {
    if (typeof theValue !== 'string') {
      return String(theValue)
    }
    return theValue.replace(/&/g, CUSTOM_ENCODED_AMPERSAND)
  }

  private decodeValue (theValue: string): string {
    return theValue.replace(new RegExp(CUSTOM_ENCODED_AMPERSAND, 'g'), '&')
  }
}

export default UrlHashSearchParams
